import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Switch,
  Input,
  message,
  Divider,
  Alert,
  Table,
  Tag
} from 'antd';
import {
  GlobalOutlined,
  DollarOutlined,
  TranslationOutlined,
  SettingOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

interface LanguageSettings {
  default_language: string;
  supported_languages: string[];
  auto_detect: boolean;
  fallback_language: string;
}

interface CurrencySettings {
  default_currency: string;
  supported_currencies: string[];
  auto_convert: boolean;
  exchange_rate_api: string;
}

interface Props {
  data: any;
  onRefresh: () => void;
}

const LanguageCurrency: React.FC<Props> = ({ data, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [languageSettings, setLanguageSettings] = useState<LanguageSettings>({
    default_language: 'en',
    supported_languages: ['en', 'fr', 'zh'],
    auto_detect: true,
    fallback_language: 'en'
  });
  const [currencySettings, setCurrencySettings] = useState<CurrencySettings>({
    default_currency: 'USD',
    supported_currencies: ['USD', 'NGN', 'EUR'],
    auto_convert: false,
    exchange_rate_api: ''
  });

  const [form] = Form.useForm();

  // Supported languages with full details
  const availableLanguages = [
    {
      code: 'en',
      name: 'English',
      native_name: 'English',
      flag: '🇺🇸',
      rtl: false,
      completion: 100
    },
    {
      code: 'fr',
      name: 'French',
      native_name: 'Français',
      flag: '🇫🇷',
      rtl: false,
      completion: 95
    },
    {
      code: 'zh',
      name: 'Chinese (Simplified)',
      native_name: '简体中文',
      flag: '🇨🇳',
      rtl: false,
      completion: 90
    },
    {
      code: 'ar',
      name: 'Arabic',
      native_name: 'العربية',
      flag: '🇸🇦',
      rtl: true,
      completion: 85
    },
    {
      code: 'es',
      name: 'Spanish',
      native_name: 'Español',
      flag: '🇪🇸',
      rtl: false,
      completion: 95
    },
    {
      code: 'de',
      name: 'German',
      native_name: 'Deutsch',
      flag: '🇩🇪',
      rtl: false,
      completion: 90
    }
  ];

  // Supported currencies
  const availableCurrencies = [
    {
      code: 'USD',
      name: 'US Dollar',
      symbol: '$',
      flag: '🇺🇸',
      rate: 1.0
    },
    {
      code: 'NGN',
      name: 'Nigerian Naira',
      symbol: '₦',
      flag: '🇳🇬',
      rate: 1650.0
    },
    {
      code: 'EUR',
      name: 'Euro',
      symbol: '€',
      flag: '🇪🇺',
      rate: 0.85
    },
    {
      code: 'GBP',
      name: 'British Pound',
      symbol: '£',
      flag: '🇬🇧',
      rate: 0.73
    },
    {
      code: 'JPY',
      name: 'Japanese Yen',
      symbol: '¥',
      flag: '🇯🇵',
      rate: 110.0
    },
    {
      code: 'CAD',
      name: 'Canadian Dollar',
      symbol: 'C$',
      flag: '🇨🇦',
      rate: 1.25
    }
  ];

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // API call will be implemented
      // For now using default settings
    } catch (error) {
      message.error('Failed to load language and currency settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    form.setFieldsValue({
      ...languageSettings,
      ...currencySettings
    });
  }, []);

  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      
      const newLanguageSettings: LanguageSettings = {
        default_language: values.default_language,
        supported_languages: values.supported_languages,
        auto_detect: values.auto_detect,
        fallback_language: values.fallback_language
      };

      const newCurrencySettings: CurrencySettings = {
        default_currency: values.default_currency,
        supported_currencies: values.supported_currencies,
        auto_convert: values.auto_convert,
        exchange_rate_api: values.exchange_rate_api
      };

      setLanguageSettings(newLanguageSettings);
      setCurrencySettings(newCurrencySettings);

      message.success('Language and currency settings updated successfully');
      onRefresh();
    } catch (error) {
      message.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const languageColumns = [
    {
      title: 'Language',
      key: 'language',
      render: (record: any) => (
        <Space>
          <span style={{ fontSize: '20px' }}>{record.flag}</span>
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.native_name}</div>
          </div>
        </Space>
      )
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <Tag>{code.toUpperCase()}</Tag>
    },
    {
      title: 'Direction',
      dataIndex: 'rtl',
      key: 'rtl',
      render: (rtl: boolean) => (
        <Tag color={rtl ? 'orange' : 'blue'}>
          {rtl ? 'RTL' : 'LTR'}
        </Tag>
      )
    },
    {
      title: 'Translation Progress',
      dataIndex: 'completion',
      key: 'completion',
      render: (completion: number) => (
        <div>
          <Text>{completion}%</Text>
          <div style={{ width: '100px', height: '6px', backgroundColor: '#f0f0f0', borderRadius: '3px', marginTop: '4px' }}>
            <div 
              style={{ 
                width: `${completion}%`, 
                height: '100%', 
                backgroundColor: completion >= 95 ? '#52c41a' : completion >= 90 ? '#faad14' : '#ff4d4f',
                borderRadius: '3px'
              }} 
            />
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: any) => {
        const isSupported = languageSettings.supported_languages.includes(record.code);
        const isDefault = languageSettings.default_language === record.code;
        return (
          <Space direction="vertical" size="small">
            {isDefault && <Tag color="gold">Default</Tag>}
            {isSupported && <Tag color="green">Enabled</Tag>}
            {!isSupported && <Tag color="default">Available</Tag>}
          </Space>
        );
      }
    }
  ];

  const currencyColumns = [
    {
      title: 'Currency',
      key: 'currency',
      render: (record: any) => (
        <Space>
          <span style={{ fontSize: '20px' }}>{record.flag}</span>
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.symbol}</div>
          </div>
        </Space>
      )
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <Tag>{code}</Tag>
    },
    {
      title: 'Exchange Rate (to USD)',
      dataIndex: 'rate',
      key: 'rate',
      render: (rate: number) => (
        <Text>{rate === 1 ? '1.00' : rate.toFixed(2)}</Text>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: any) => {
        const isSupported = currencySettings.supported_currencies.includes(record.code);
        const isDefault = currencySettings.default_currency === record.code;
        return (
          <Space direction="vertical" size="small">
            {isDefault && <Tag color="gold">Default</Tag>}
            {isSupported && <Tag color="green">Enabled</Tag>}
            {!isSupported && <Tag color="default">Available</Tag>}
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          ...languageSettings,
          ...currencySettings
        }}
      >
        {/* Language Settings */}
        <Card 
          title={
            <Space>
              <GlobalOutlined />
              Language Settings
            </Space>
          }
          style={{ marginBottom: '24px' }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="default_language"
                label="Default Language"
                rules={[{ required: true, message: 'Please select default language' }]}
              >
                <Select>
                  {availableLanguages.map(lang => (
                    <Option key={lang.code} value={lang.code}>
                      <Space>
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                        <Text type="secondary">({lang.native_name})</Text>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="fallback_language"
                label="Fallback Language"
                rules={[{ required: true, message: 'Please select fallback language' }]}
              >
                <Select>
                  {availableLanguages.map(lang => (
                    <Option key={lang.code} value={lang.code}>
                      <Space>
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="supported_languages"
            label="Supported Languages"
            rules={[{ required: true, message: 'Please select supported languages' }]}
          >
            <Select mode="multiple">
              {availableLanguages.map(lang => (
                <Option key={lang.code} value={lang.code}>
                  <Space>
                    <span>{lang.flag}</span>
                    <span>{lang.name}</span>
                    <Text type="secondary">({lang.completion}% complete)</Text>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="auto_detect"
            label="Auto-detect User Language"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Alert
            message="Language Support"
            description="English, French, and Chinese are fully supported. Additional languages can be enabled but may require translation updates."
            type="info"
            style={{ marginTop: '16px' }}
          />
        </Card>

        {/* Currency Settings */}
        <Card 
          title={
            <Space>
              <DollarOutlined />
              Currency Settings
            </Space>
          }
          style={{ marginBottom: '24px' }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="default_currency"
                label="Default Currency"
                rules={[{ required: true, message: 'Please select default currency' }]}
              >
                <Select>
                  {availableCurrencies.map(curr => (
                    <Option key={curr.code} value={curr.code}>
                      <Space>
                        <span>{curr.flag}</span>
                        <span>{curr.name} ({curr.symbol})</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="supported_currencies"
                label="Supported Currencies"
                rules={[{ required: true, message: 'Please select supported currencies' }]}
              >
                <Select mode="multiple">
                  {availableCurrencies.map(curr => (
                    <Option key={curr.code} value={curr.code}>
                      <Space>
                        <span>{curr.flag}</span>
                        <span>{curr.name} ({curr.symbol})</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="auto_convert"
            label="Auto-convert Prices"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="exchange_rate_api"
            label="Exchange Rate API (Optional)"
          >
            <Input placeholder="https://api.exchangerate-api.com/v4/latest/USD" />
          </Form.Item>

          <Alert
            message="Currency Conversion"
            description="USD and NGN are natively supported for billing. Other currencies will be converted using real-time exchange rates."
            type="info"
            style={{ marginTop: '16px' }}
          />
        </Card>

        {/* Available Languages Table */}
        <Card 
          title="Available Languages" 
          style={{ marginBottom: '24px' }}
        >
          <Table
            columns={languageColumns}
            dataSource={availableLanguages}
            rowKey="code"
            pagination={false}
            size="small"
          />
        </Card>

        {/* Available Currencies Table */}
        <Card 
          title="Available Currencies"
          style={{ marginBottom: '24px' }}
        >
          <Table
            columns={currencyColumns}
            dataSource={availableCurrencies}
            rowKey="code"
            pagination={false}
            size="small"
          />
        </Card>

        {/* Save Button */}
        <Card>
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<SettingOutlined />}
              >
                Save Language & Currency Settings
              </Button>
              <Button onClick={() => form.resetFields()}>
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Card>
      </Form>
    </div>
  );
};

export default LanguageCurrency;
